import { Link } from '@ticketmaster/aurora';
import ButtonComponent from '@ticketmaster/tm1pos-web-shared/components/ButtonComponent';
import { Card, ERROR as CardError } from '@ticketmaster/tm1pos-web-shared/components/Card';
import { currencyFormatOptions, SYSTEM_ID } from '@ticketmaster/tm1pos-web-shared/constants';
import { MarketType } from '@ticketmaster/tm1pos-web-shared/model/market-type';
import { generateOrderDetailsLink } from '@ticketmaster/tm1pos-web-shared/utils/customer-utils';
import { formatDatesAndTimes } from '@ticketmaster/tm1pos-web-shared/utils/dateUtils';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, type InjectedIntlProps, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ARCHTICS_EVENT_TYPE } from '../../../EventDetailPage/constants';
import checkoutMessages from '../../messages';
import { CARD, CASH } from '../form/constants';
import { DeliveryRow } from './components/delivery-row/delivery-row';
import messages from './messages';
import './styles.scss';
import { selectOrderConfirmationState } from './order-confirmation-selector';

type OrderConfirmationProps = {
  continueShopping: () => void;
} & InjectedIntlProps;

export const OrderConfirmation = ({ continueShopping, intl }: OrderConfirmationProps) => {
  const { order, localPrinterError, selectedPrinter, currentEvent, hostName, isAuthorizationOff } =
    useSelector(selectOrderConfirmationState);

  const orderTimeFormatted = formatDatesAndTimes(order.orderTime, order.orderTimeUtcOffset, intl.locale);
  const orderTime = `${orderTimeFormatted.fullDateShortMonth} • ${orderTimeFormatted.time}`;

  const orderUrl = useMemo(() => {
    const isArchtics = currentEvent.type === ARCHTICS_EVENT_TYPE;
    const accountId = isArchtics ? order.orderId : 0;
    const rawOrderId = isArchtics
      ? String(order.ticketsData.id).replace(/^ARX-/, '')
      : `${order.orderId}/${hostName || ''}`;
    const orderId = `${isArchtics ? SYSTEM_ID.ARCHTICS : SYSTEM_ID.HOST}|${rawOrderId}|${currentEvent.id}`;

    return `/customers${generateOrderDetailsLink(accountId, orderId, MarketType.PRIMARY)}`;
  }, [currentEvent, hostName, order?.orderId, order?.ticketsData?.id]);

  const paymentsList = useMemo(
    () =>
      order.payments.map((payment: any) => {
        const paymentAmount = payment.amount
          ? intl.formatNumber(payment.amount.amount, {
              ...currencyFormatOptions,
              currency: payment.amount.currency,
            })
          : '';
        const paymentMethodLabel =
          payment.type === CASH ? intl.formatMessage(checkoutMessages.checkoutCash) : payment.data;
        let paymentData = '';
        if (currentEvent.isHost && payment.type === CARD && isAuthorizationOff) {
          // @ts-expect-error TODO: refactor to use intl.formatMessage
          paymentData = <FormattedMessage {...messages.paymentNotProcessed} values={{ paymentData: payment.data }} />;
        } else {
          paymentData = paymentAmount ? `${paymentMethodLabel} / ${paymentAmount}` : paymentMethodLabel;
        }

        return (
          <div className="order-detail__row" key={paymentData}>
            <div className="order-detail__col order-detail__col--left">
              <FormattedMessage {...messages.payment} /> {payment.name}
            </div>
            <div className="order-detail__col order-detail__col--right">{paymentData}</div>
          </div>
        );
      }),
    [currentEvent, isAuthorizationOff, intl, order?.payments],
  );

  const localPrinterErrorComponent = useCallback(() => {
    if (!localPrinterError) {
      return null;
    }

    const { title, body, suggestion } = localPrinterError;
    const titleValues =
      selectedPrinter && selectedPrinter.name
        ? // @ts-expect-error missing type
          Object.assign(title?.values || {}, {
            printer: selectedPrinter.name,
          })
        : // @ts-expect-error missing type
          title?.values;

    const formattedTitleValues: Record<string, string | React.ReactNode> = {};
    Object.keys(titleValues).forEach((titleKey) => {
      const message = titleValues[titleKey];
      if (typeof message === 'string') {
        formattedTitleValues[titleKey] = message;
      } else {
        formattedTitleValues[titleKey] = <FormattedMessage {...message} />;
      }
    });

    // @ts-expect-error missing type
    const bodyValues = Object.assign(body?.values || {}, {
      // @ts-expect-error missing type
      suggestion: suggestion ? <FormattedMessage {...suggestion} /> : '',
      direction: <FormattedMessage {...messages.directionAfterPrintError} />,
    });
    // @ts-expect-error missing type
    const header = <FormattedMessage {...title} values={formattedTitleValues} />;
    // @ts-expect-error missing type
    const childs = body ? <FormattedMessage {...body} values={bodyValues} /> : null;
    return (
      <div className="order-detail__row">
        <Card type={CardError} header={header}>
          {childs}
        </Card>
      </div>
    );
  }, [localPrinterError, selectedPrinter]) as any;

  return (
    <div className="sdr-order-conf">
      <div className="sdr-order-conf__details">
        <div className="sdr-order-conf__order">
          <div className="order-detail__row">
            <div className="order-detail__col order-detail__col--left">
              <FormattedMessage {...messages.orderId} />
            </div>
            <div className="order-detail__col order-detail__col--right">
              <Link href={orderUrl} weight="semiBold" tabIndex={0}>
                {order.orderId}
              </Link>
            </div>
          </div>
          <div className="order-detail__row">
            <div className="order-detail__col order-detail__col--left">
              <FormattedMessage {...messages.orderTime} />
            </div>
            <div className="order-detail__col order-detail__col--right">{orderTime}</div>
          </div>
          {paymentsList}
          <DeliveryRow
            delivery={order.delivery}
            deliveryMethod={order.deliveryMethod}
            deliverySuccess={order.deliverySuccess}
            intl={intl}
          />
          {order.customerName ? (
            <div className="order-detail__row">
              <div className="order-detail__col order-detail__col--left">
                <FormattedMessage {...messages.name} />
              </div>
              <div className="order-detail__col order-detail__col--right">{order.customerName}</div>
            </div>
          ) : null}
          {localPrinterErrorComponent()}
        </div>
      </div>
      <div className="sdr-order-conf__btn">
        <ButtonComponent className="oc-btn" onButtonClick={continueShopping}>
          <FormattedMessage {...messages.continueSelling} />
        </ButtonComponent>
      </div>
    </div>
  );
};

export default injectIntl(OrderConfirmation);
